import React, { useMemo } from 'react';
import { trpc } from '@/utils/trpc-client';
import { PageTitle } from '@/features/shared/page-title';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/forms/ui/card';
import { format } from 'date-fns';
import { clsx } from 'clsx';
import { AlertCircle, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';
import { useFlagEnabled } from '@/utils/feature-flags';
import type { FreshnessStatus, PlatformFreshnessItem } from 'shared/src/trpc-types/freshness-types';
import { NaBadge, StatusBadge, SummaryPills } from './freshness-components';
import {
  buildSortedRows,
  computeOverallWorst,
  computeStatusCounts,
  formatTimestampWithTz,
  FRESHNESS_QUERY_OPTIONS
} from './freshness-utils';

export function DataFreshness() {
  const { data, isPending, isError, dataUpdatedAt } = trpc.dataFreshnessStatus.useQuery(
    undefined,
    FRESHNESS_QUERY_OPTIONS
  );
  const activityEnabled = useFlagEnabled('activity-log-v1');
  const buildOptions = useMemo(() => ({ includeActivity: activityEnabled }), [activityEnabled]);

  const statusCounts = useMemo(
    () => (data ? computeStatusCounts(data, buildOptions) : null),
    [data, buildOptions]
  );
  const overallWorst = useMemo(
    () => (statusCounts ? computeOverallWorst(statusCounts) : null),
    [statusCounts]
  );
  const sortedRows = useMemo(
    () => (data ? buildSortedRows(data, buildOptions) : []),
    [data, buildOptions]
  );
  const lastChecked = useMemo(() => formatTimestampWithTz(dataUpdatedAt), [dataUpdatedAt]);

  if (isPending) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-muted-foreground">Loading...</span>
      </div>
    );
  }

  // Only show full error UI if no data at all (initial load failure)
  // Otherwise show data with error banner (transient refetch failure)
  if (isError && !data) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2">
        <span className="text-red-600">Failed to load data freshness status</span>
        <span className="text-sm text-muted-foreground">Please try again or contact support.</span>
      </div>
    );
  }

  if (!statusCounts || !overallWorst) return null;

  return (
    <div className="p-6">
      <PageTitle title="Data Freshness Status" />
      <p className="mb-1 text-xs text-muted-foreground">Last checked: {lastChecked}</p>
      <h1 className="mb-2 text-2xl font-bold">Data Freshness Status</h1>
      <p className="mb-4 text-muted-foreground">
        Platform and Zoho data sync nightly; the activity log refreshes every few minutes. Each
        platform is expected through yesterday, the last complete day, and never past the last day
        it had a campaign in flight. "Fresh" means the source has reached that expected date. "Idle"
        means nothing is currently in flight, so the platform is not expected to produce new data
        and is not counted as a problem.
      </p>

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to refresh. Showing last known state.
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="py-3">Data Sources</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Executive Summary Card */}
          <div
            className={clsx(
              'mb-6 rounded-xl border-2 p-5',
              overallWorst === 'critical' && 'border-red-400 bg-red-50',
              overallWorst === 'stale' && 'border-amber-400 bg-amber-50',
              overallWorst === 'noData' && 'border-gray-400 bg-gray-50',
              overallWorst === 'healthy' && 'border-lime-400 bg-lime-50'
            )}>
            <div className="flex items-center gap-3">
              {overallWorst === 'critical' && (
                <AlertTriangle className="h-8 w-8 text-red-600" strokeWidth={2.5} />
              )}
              {overallWorst === 'stale' && (
                <AlertCircle className="h-8 w-8 text-amber-600" strokeWidth={2.5} />
              )}
              {overallWorst === 'noData' && (
                <HelpCircle className="h-8 w-8 text-gray-500" strokeWidth={2.5} />
              )}
              {overallWorst === 'healthy' && (
                <CheckCircle2 className="h-8 w-8 text-lime-600" strokeWidth={2.5} />
              )}
              <div>
                <h3
                  className={clsx(
                    'text-xl font-bold',
                    overallWorst === 'critical' && 'text-red-800',
                    overallWorst === 'stale' && 'text-amber-800',
                    overallWorst === 'noData' && 'text-gray-700',
                    overallWorst === 'healthy' && 'text-lime-800'
                  )}>
                  {overallWorst === 'critical' && `${statusCounts.critical} Critical Issues`}
                  {overallWorst === 'stale' && `${statusCounts.stale} Sources Stale`}
                  {overallWorst === 'noData' && `${statusCounts.noData} Sources Missing Data`}
                  {overallWorst === 'healthy' && 'All Systems Fresh'}
                </h3>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  <SummaryPills counts={statusCounts} />
                </div>
              </div>
            </div>
          </div>

          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground">
                <th className="pb-2">Source</th>
                <th className="pb-2">Last Data Date</th>
                <th className="pb-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(row => {
                switch (row.kind) {
                  case 'platform':
                    return (
                      <PlatformFreshnessRow
                        key={row.name}
                        name={row.name}
                        impressions={row.impressions}
                        video={row.video}
                        lastDataDate={row.lastDataDate}
                        activeBuyCount={row.activeBuyCount}
                        expectedDataDate={row.expectedDataDate}
                      />
                    );
                  case 'zoho':
                    return (
                      <ZohoFreshnessRow
                        key="zoho"
                        name={row.name}
                        date={row.lastModified}
                        status={row.status}
                        daysBehind={row.daysBehind}
                      />
                    );
                  case 'activity':
                    return (
                      <ActivityFreshnessRow
                        key="activity"
                        name={row.name}
                        status={row.status}
                        minutesBehind={row.minutesBehind}
                        lastActivityAt={row.lastActivityAt}
                        lastRefreshAt={row.lastRefreshAt}
                      />
                    );
                  default: {
                    const _exhaustive: never = row;
                    return _exhaustive;
                  }
                }
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function PlatformFreshnessRow({
  name,
  impressions,
  video,
  lastDataDate,
  activeBuyCount,
  expectedDataDate
}: {
  name: string;
  impressions: PlatformFreshnessItem['impressions'];
  video: PlatformFreshnessItem['video'];
  lastDataDate: Date | null;
  activeBuyCount: number | undefined;
  expectedDataDate: Date | null | undefined;
}) {
  return (
    <tr className="border-b transition-colors last:border-0 hover:bg-muted/50">
      <td className="py-3 font-medium">
        {name}
        {/* Shown for every platform, not just idle ones: this count is what
            suppresses a stale badge, so it has to stay visible to be trusted. */}
        {activeBuyCount !== undefined && (
          <div className="text-xs font-normal text-muted-foreground">
            {activeBuyCount === 0
              ? 'no buys in flight'
              : `${activeBuyCount} ${activeBuyCount === 1 ? 'buy' : 'buys'} in flight`}
          </div>
        )}
      </td>
      <td className="py-3">
        {lastDataDate ? (
          <time dateTime={lastDataDate.toISOString()}>{format(lastDataDate, 'MMM d, yyyy')}</time>
        ) : (
          <span className="text-muted-foreground">Never</span>
        )}
        {/* The yardstick every badge on this row is measured against — shown so a
            status can be checked rather than taken on faith. */}
        {expectedDataDate && (
          <div className="text-xs text-muted-foreground">
            expected through {format(expectedDataDate, 'MMM d')}
          </div>
        )}
      </td>
      <td className="py-3">
        <div className="flex flex-wrap gap-1.5">
          <StatusBadge
            status={impressions.status}
            amountBehind={impressions.daysBehind}
            prefix="Imp"
          />
          {video ? (
            <StatusBadge status={video.status} amountBehind={video.daysBehind} prefix="Video" />
          ) : (
            <NaBadge prefix="Video" />
          )}
        </div>
      </td>
    </tr>
  );
}

function ZohoFreshnessRow({
  name,
  date,
  status,
  daysBehind
}: {
  name: string;
  date: Date | null;
  status: FreshnessStatus;
  daysBehind: number;
}) {
  return (
    <tr className="border-b transition-colors last:border-0 hover:bg-muted/50">
      <td className="py-3 font-medium">{name}</td>
      <td className="py-3">
        {date ? (
          <time dateTime={date.toISOString()}>{formatTimestampWithTz(date)}</time>
        ) : (
          <span className="text-muted-foreground">Never</span>
        )}
      </td>
      <td className="py-3">
        <StatusBadge status={status} amountBehind={daysBehind} />
      </td>
    </tr>
  );
}

function ActivityFreshnessRow({
  name,
  status,
  minutesBehind,
  lastActivityAt,
  lastRefreshAt
}: {
  name: string;
  status: FreshnessStatus;
  minutesBehind: number;
  lastActivityAt: Date | null;
  lastRefreshAt: Date | null;
}) {
  const showRefreshSubLabel = status === 'stale' || status === 'very_stale';
  return (
    <tr className="border-b transition-colors last:border-0 hover:bg-muted/50">
      <td className="py-3 font-medium">{name}</td>
      <td className="py-3">
        {lastActivityAt ? (
          <time dateTime={lastActivityAt.toISOString()}>
            {formatTimestampWithTz(lastActivityAt)}
          </time>
        ) : (
          <span className="text-muted-foreground">Never</span>
        )}
        {showRefreshSubLabel ? (
          <div className="text-xs text-muted-foreground">
            last run: {lastRefreshAt ? formatTimestampWithTz(lastRefreshAt) : 'Never'}
          </div>
        ) : null}
      </td>
      <td className="py-3">
        <StatusBadge status={status} amountBehind={minutesBehind} unit="m" />
      </td>
    </tr>
  );
}
